import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * オンライン面会スケジュール API 操作クラス
 */
export default class OnlineReservationSchedules {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET OnlineReservationSchedules
   */
  public async get(userStore: LoginUser, groupId: number, year: string, month: string): Promise<Array<OnlineReservationSchedulesModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId,
      year: year,
      month: month
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OnlineReservationSchedules, formatParams);
    const response = await this.commonService.executeGet<Array<OnlineReservationSchedulesModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      {},
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * POST OnlineReservationSchedules
   */
  public async post(userStore: LoginUser, groupId: number, year: string, month: string, requestBody: OnlineReservationSchedulesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId,
      year: year,
      month: month
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OnlineReservationSchedules, formatParams);

    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH OnlineReservationSchedules
   */
  public async patch(userStore: LoginUser, groupId: number, year: string, month: string, requestBody: OnlineReservationSchedulesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId,
      year: year,
      month: month
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OnlineReservationSchedules, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

/**
 * ### オンライン面会スケジュールモデル
 */
export interface OnlineReservationSchedulesModel {
  /** 施設ID */
  facilityId: number;
  /** 予約可能日時ソートキー */
  onlineReservationsScheduleSortKey?: string;
  /** 年 */
  year: number;
  /** 月 */
  month: number;
  /** 日 */
  date: number;
  /** 時 */
  hour: number;
  /** 分 */
  min: number;
  /** 予約可能か否か */
  canReservation: boolean;
  /** 予約依頼数 */
  reservationRequestsCount: number;
  /** 登録ユーザーID */
  registerUserId: string;
  /** 登録ユーザー名 */
  registerUserName: string;
  /** 削除フラグ */
  deleteFlag: boolean;
  /** 更新日時 */
  updated: number;
}

/**
 * ### オンライン面会スケジュールPOST/PUT
 */
export interface OnlineReservationSchedulesPostModel {
  /**
   * 予約可能か否か
   * 日付、時間の情報を持つ2次元配列
   * 配列0番目から1日、2日、3日・・・
   * 配列0番目から08:00(～09:00)、09:00(～10:00)、10:00(～11:00)・・・
   */
  canReservations: Array<Array<boolean>>;
  /** 登録ユーザーID */
  registerUserId: string;
  /** 登録ユーザー名 */
  registerUserName: string;
  /** 整合性チェック用更新日時 */
  updated: number;
}
